import api from './api';
import { handleApiError } from './apiError';

export type UserProfile = {
  // No screen renders this. It's the identity handed to Sentry (see
  // sessionStore's `emit`), so don't drop it as unused wire noise.
  id: string;
  isProfileComplete: boolean;
  mobile: string;
  email: string | null;
  firstName: string;
  lastName: string;
  fullName: string;
  /**
   * The one social link the user has shared, or null. Prefills the sheet an
   * approval-only ticket tier opens. NOT sent by the verify responses, so it
   * reads null until a `/me` (session restore) or a `PATCH /me` fills it in.
   */
  socialMediaLink: string | null;
  /**
   * The ENTITLED membership, or null. An unconfirmed purchase lands on the field
   * below instead, so `!!membership` IS "is this user a member".
   */
  membership: UserMembership | null;
  /**
   * A purchase that exists at the gateway and hasn't been authorized. Never set
   * at the same time as `membership` — `/me` reports one field, not two.
   */
  pendingMembership: MembershipBody | null;
  /** The one-shot pass is once per user, ever. */
  hasUsedUpTrialPass: boolean;
  /** Has a name, no email attempted yet. */
  isEmailMissing: boolean;
  isOnlyEmailVerificationPending: boolean;
  areDetailsMissing: boolean;
};

export type AuthenticatedUser = UserProfile & { token: string };

type ChallengeBody = {
  challengeId: string;
};

/**
 * One booking category's allowance under a membership.
 *
 * Discriminated on `isUnlimited` because the wire OMITS `limit` and `remaining`
 * when it's true — reading either without checking the flag first gets an
 * `undefined`, not a big number. Every category on the one live plan reads
 * unlimited today, so that omission is the common case, not the edge one.
 *
 * `canBook` is NOT `remaining > 0`: it also folds in an active booking penalty,
 * which switches every category off at once while the counts still look fine.
 * Gate a button on it rather than on the arithmetic.
 */
export type PerkUsage = {
  /** The category's display name, authored server-side to be shown as-is. */
  label: string;
  bookings: number;
  canBook: boolean;
} & (
  | { isUnlimited: true }
  | { isUnlimited: false; limit: number; remaining: number }
);

/**
 * The allowance, and when it comes back.
 *
 * `perks` is every gated booking category, keyed by the server's own event-kind
 * strings (`normal` / `ps_original` / `preview` / `secret_menu` today).
 * Deliberately an open map, not a fixed set of fields: the backend can add a
 * category without a client release, so read what's in it rather than reaching
 * for a name. Those keys are DATA and survive [[api]]'s casing transform
 * untouched — see `DATA_KEY_PATHS` there.
 *
 * The wire also sends `calculatedSince`, the current cycle's start. Nothing
 * draws it.
 */
export type PerksUsage = {
  /**
   * When the counts go back to zero — the same instant as `renewsAt`. Null when
   * nothing will reset them (a coupon grant, a cancelled subscription): the
   * allowance doesn't reset, it stops.
   */
  resetsAt: string | null;
  perks: Record<string, PerkUsage>;
};

/**
 * The plan a membership is ON.
 *
 * These used to sit at the TOP level of the membership block and moved into
 * this nested one; the old keys are not aliased, so reading `membership.name`
 * now gets `undefined` rather than throwing.
 *
 * The wire also sends `isRecurring` and `billingInterval` here. Each is one line
 * to add back; nothing reads them off a HELD membership — the catalog's own
 * [[Membership]] is where a plan's terms get rendered.
 */
export type MembershipPlanRef = {
  /**
   * The plan's own id, which is what joins a held membership to a catalog row.
   * Note the catalog can't always answer: it only returns `is_active` plans, and
   * a member can be sitting on one that was deactivated under them.
   */
  id: string;
  name: string;
  /**
   * The named access tier — a free CharField, reading `access` on the one plan
   * staging still activates, where it used to read `access_pass`. Also what a
   * ticket tier's `allowedMembershipTiers` matches against.
   */
  tier: string;
  /** The trial-or-not test — `tier` is free text, this is the enum. */
  kind: 'trial' | 'access_pass';
};

/**
 * The gateway lifecycle. Only `pending` is waited on ([[usePollMembership]]);
 * `cancelled` / `completed` / `expired` are terminal, and the four in between
 * are all states a member can sit in while STILL entitled — which is why
 * entitlement is `phase`'s answer below, not this field's.
 *
 * Stays snake_case: [[api]] converts keys, not values.
 */
export type MembershipStatus =
  | 'pending'
  | 'active'
  | 'cancelling'
  | 'cancelled'
  | 'paused'
  | 'pending_retry'
  | 'halted'
  | 'completed'
  | 'expired';

/**
 * A membership in full — the block `/me` reports for an entitled one, and the
 * whole of what `/me/memberships` and `/me/memberships/{id}` return (the server
 * builds all three from one helper, so they cannot drift).
 *
 * The wire also sends `subscriptionId` and `authorizedAt` — documented as when
 * the mandate was signed, in practice `activatedAt` on every ordinary purchase.
 * It sends a `checkoutPayload` too, modelled on [[MembershipDetail]] rather than
 * here: it's null on anything but a `pending` row, and the manage screen is the
 * only place one gets reopened.
 */
export type UserMembership = {
  id: string;
  status: MembershipStatus;
  /**
   * How it was funded. A `coupon` or `grant` has no subscription behind it, so it
   * expires instead of renewing and cancels without touching the gateway. That's
   * the distinction a manage screen branches on; it used to have to be inferred
   * from a null subscription id.
   */
  source: 'payment' | 'coupon' | 'grant';
  /**
   * Where it sits in time, derived server-side from the status and the latest
   * billing cycle so the client doesn't reimplement the rule. THIS is
   * entitlement, not `status`: a member who cancels keeps their benefits to the
   * end of the cycle they paid for, so a `cancelled` membership reads `current`
   * until that date and only then flips to `past`.
   */
  phase: 'current' | 'upcoming' | 'past';
  plan: MembershipPlanRef;
  activatedAt: string | null;
  renewsAt: string | null;
  expiresAt: string | null;
  daysRemaining: number | null;
  /**
   * The day cancellation was ASKED for, weeks before access ends. Optional
   * because the wire OMITS the key — rather than sending null — on everything
   * that isn't a `cancelled` membership carrying a timestamp, `cancelling`
   * included.
   */
  cancelledAt?: string;
  /**
   * The ENTITLEMENT — what this membership may still book, not what it cost.
   *
   * NULL unless the status is exactly `active`, which sweeps up memberships that
   * are still fully entitled (`cancelling`, `pending_retry`). So it means
   * "unknown", never "nothing left" — gate a button on `phase` and fall back to
   * letting them try, not on the absence of this block.
   */
  perksUsage: PerksUsage | null;
};

/**
 * `/me`'s in-flight membership: a purchase that exists at the gateway and has
 * not been authorized yet. Three keys on the wire and nothing else — no plan, no
 * dates, no entitlement — so it is a DIFFERENT shape from [[UserMembership]],
 * not a sparse one. Slim only HERE: `/me/memberships` sends the full block for a
 * pending row, at `phase: 'upcoming'`.
 *
 * The third key is `checkoutPayload`, the `{key, subscriptionId, shortUrl}`
 * block `POST /membership` was answered with, re-readable for as long as the
 * membership is pending. Not modelled here, because the resume is offered on the
 * membership's own page rather than off this block: `id` is enough to get there,
 * and [[MembershipDetail]] carries the payload along with the plan's name, which
 * this slim block doesn't have.
 */

// The membership block as it arrives: the perk map is the one part [[api]] does
// not camelCase, so its leaves are still the server's spelling until
// `toUserMembership` maps them. `label` is one word, so it survives either way.
type PerkUsageBody = {
  label: string;
  is_unlimited: boolean;
  bookings: number;
  can_book: boolean;
  limit?: number;
  remaining?: number;
};

export type MembershipBody = Omit<UserMembership, 'perksUsage'> & {
  perksUsage: {
    resetsAt: string | null;
    perks: Record<string, PerkUsageBody>;
  } | null;
};

/**
 * `/me`'s single `membership` key as it arrives — entitled, in flight, or null,
 * before the perk map is mapped. [[splitMembership]] is what takes the union
 * apart, so nothing downstream of `toUserProfile` sees it.
 */

const toPerkUsage = (body: PerkUsageBody): PerkUsage => {
  const named = {
    label: body.label,
    bookings: body.bookings,
    canBook: body.can_book,
  };

  if (body.is_unlimited) return { ...named, isUnlimited: true };

  // A capped category that arrives with no cap reads as blocked — the safe half
  // of a body that shouldn't happen.
  return {
    ...named,
    isUnlimited: false,
    limit: body.limit ?? 0,
    remaining: body.remaining ?? 0,
  };
};

/**
 * The full membership block, with its perk map labelled. Shared with
 * [[membershipsApi]]'s `getMyMembership`, which reads the same block from its
 * own endpoint.
 */
export const toUserMembership = (body: MembershipBody): UserMembership => ({
  ...body,
  perksUsage: body.perksUsage && {
    resetsAt: body.perksUsage.resetsAt,
    perks: Object.fromEntries(
      Object.entries(body.perksUsage.perks).map(([key, perk]) => [
        key,
        toPerkUsage(perk),
      ])
    ),
  },
});

/**
 * One wire field into two: the entitled membership and the purchase in flight.
 *
 * The split is here so that every gate downstream is a null check again —
 * `/me` reports a purchase in the same field from the moment it's CREATED, which
 * made `!!user.membership` read true before any money had moved. `plan` is the
 * discriminant: the in-flight block carries no plan, no dates and no perks to
 * reason with, and needs no mapping either.
 */
const splitMembership = (
  body: MembershipBody | null
): Pick<UserProfile, 'membership' | 'pendingMembership'> => {
  if (body === null) return { membership: null, pendingMembership: null };
  return body.status !== 'pending'
    ? { membership: toUserMembership(body), pendingMembership: null }
    : { membership: null, pendingMembership: body };
};

type UserBody = {
  id: string;
  isProfileComplete: boolean;
  mobile: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  isEmailVerified: boolean;
  // Optional because the verify responses omit the key entirely; `/me` and
  // `PATCH /me` both send it.
  socialMediaLink?: string | null;
  membership: MembershipBody | null;
  hasUsedTrial: boolean;
};

type VerifyBody = UserBody & { token: string };

const toUserProfile = (body: UserBody): UserProfile => ({
  id: body.id,
  isProfileComplete: body.isProfileComplete,
  ...splitMembership(body.membership),
  mobile: body.mobile,
  email: body.email,
  firstName: body.firstName ?? '',
  lastName: body.lastName ?? '',
  fullName: [body.firstName, body.lastName].filter(Boolean).join(' '),
  socialMediaLink: body.socialMediaLink ?? null,
  isEmailMissing: !!body.firstName && !body.email,
  isOnlyEmailVerificationPending: !!(
    body.firstName &&
    body.email &&
    !body.isEmailVerified
  ),
  areDetailsMissing: !(body.firstName && body.lastName),
  hasUsedUpTrialPass: body.hasUsedTrial,
});

// PATCH /me returns the full user object — same shape as `/me` itself, plus
// one extra top-level key. `challengeId` is ABSENT when no email OTP went
// out, which is the signal updateMe converts below.
type PatchMeBody = UserBody & {
  challengeId?: string;
};

/**
 * Ask for an SMS code for `mobile`, whether or not it already has an account.
 *
 * This is the one place the API's shape is deliberately NOT mirrored 1:1. The
 * server splits "send me a code" across two endpoints and makes the client
 * discover which one applies from a status code: `POST /auth/login` 404s for an
 * unknown number, and `POST /auth/register` is the new-user path. Both return
 * the same bare challenge, and the UI's next step is identical either way — so
 * "is this a new user" is a fact about the backend's routing, not about the
 * screen, and it stays in here.
 *
 * That 404 is therefore NOT a failure and never reaches the caller. Callers get
 * a challenge or an ApiError, and don't learn a request "failed" on the way.
 *
 * The 409 retry covers the documented race: the number got registered between
 * our 404 and our register. A 409 means the account exists, which is login's
 * 200 branch, so we take it once.
 */
async function requestMobileOtp(mobile: string): Promise<string> {
  const login = await api.post<ChallengeBody>('/auth/login', { mobile });
  if (login.ok && login.data) return login.data.challengeId;
  if (login.status !== 404) handleApiError(login);

  const register = await api.post<ChallengeBody>('/auth/register', { mobile });
  if (register.ok && register.data) return register.data.challengeId;
  if (register.status !== 409) handleApiError(register);

  const retry = await api.post<ChallengeBody>('/auth/login', { mobile });
  if (retry.ok && retry.data) return retry.data.challengeId;
  handleApiError(retry);
}

/**
 * Confirm an OTP. Serves BOTH channels — the server routes on the challenge's
 * stored purpose, so nothing in the request says "mobile" or "email".
 *
 * On the mobile challenge this is what CREATES the account and mints the token,
 * so the caller must persist `token` here (see [[api]]) — the `PATCH /me` and
 * email verify that follow are authenticated. On the email challenge the same
 * token is echoed back, so there's nothing new to store.
 *
 * The four ways a code fails all arrive as 400 `wrong_arguments`, separable only
 * by `message`: wrong code, too many attempts, expired, already used. Only the
 * first is recoverable in place; show the message and let the user judge.
 */
async function verifyOtp(
  challengeId: string,
  otp: string
): Promise<AuthenticatedUser> {
  const response = await api.post<VerifyBody>('/auth/verify', {
    challengeId,
    otp,
  });
  if (!response.ok || !response.data) handleApiError(response);

  return { ...toUserProfile(response.data), token: response.data.token };
}

/**
 * Read back the signed-in user. 🔒 Needs a stored token.
 *
 * Session restore, and nothing else. On a cold start the device has a token and
 * no idea whether it's still good, so this is the one call that answers both
 * "who is this" and "is the credential live" — see [[session]]'s restore, which
 * is its only caller and runs it behind the splash screen.
 *
 * A dead token (never issued, or soft-revoked by logout) comes back 401
 * `not_authenticated`, which reaches the caller as an ApiError with
 * `status: 401`. That status is the ONLY thing that means "this token will never
 * work again" — a timeout or a 5xx says nothing about it — so it's what the
 * caller keys the token-deletion off, not the mere fact that this threw.
 */
async function fetchMe(): Promise<UserProfile> {
  const response = await api.get<UserBody>('/me', undefined, {
    // The app-wide client sets NO timeout (axios defaults to waiting forever),
    // which is survivable for a call made from a screen — there's a spinner and
    // a user who can back out. This one is made from the splash gate, where a
    // hung request means a frozen launch with no UI to escape from, so it gets
    // its own bound. Long enough not to sign out a real user on a bad mobile
    // network; short enough that a broken launch still ends.
    timeout: 5000,
  });
  if (!response.ok || !response.data) handleApiError(response);

  return toUserProfile(response.data);
}

/**
 * Re-send the code for an existing challenge — the "Didn't get a code?" button.
 * One endpoint for both channels; the channel comes from the challenge.
 *
 * ── The returned id REPLACES the one you had ──
 * Every resend destroys the previous challenge and issues a new id. Keep the old
 * one and the following verify fails with no visible cause, so the caller must
 * overwrite whatever it stored on every response.
 *
 * Note this is NOT the same as re-running the issuing call: inside 60s those
 * return the existing challenge and send nothing, which is useless to a user who
 * never got the SMS. This always dispatches. It's also not throttled server-side
 * (a known backend bug), so the client's countdown is the only limit there is.
 */
async function resendOtp(challengeId: string): Promise<string> {
  const response = await api.post<ChallengeBody>('/auth/otp/resend', {
    challengeId,
  });
  if (!response.ok || !response.data) handleApiError(response);
  return response.data.challengeId;
}

/**
 * Write any subset of the user's own profile. 🔒 Needs the token from mobile
 * verify.
 *
 * The ONE method for `PATCH /me`, mirroring the endpoint the way [[fetchMe]] and
 * [[deleteMe]] mirror theirs. It's a true partial update, so pass only the
 * fields the calling screen owns — signup's names, the auth flow's email, a
 * social link on its own, or a profile edit that changes all of them in one
 * request rather than in two that can half-land.
 *
 * (This deliberately replaced a per-intent split — `updateSocialLink`,
 * `updateProfile` — where three methods wrapped one endpoint and a caller had to
 * guess which. Intent belongs to the screen; this layer mirrors the wire.)
 *
 * ── `email` is a request to VERIFY, not an update ──
 * The address doesn't land on the user until the emailed OTP is confirmed.
 * `emailChallengeId` is null when no code went out, which is how the caller
 * knows whether an email-OTP screen is owed. (The server omits the challenge
 * when `email` wasn't part of the request, or when the address is already this
 * user's verified one.) A caller that sends no email always gets null — a
 * truthful "no OTP is pending", not a value to work around.
 *
 * ── `socialMediaLink` ──
 * The server takes it at face value — its only check is "must be a URL" — so
 * WHICH links we accept (Instagram / X / LinkedIn, and nothing else) is the
 * client's judgment: pass the output of [[normalizeSocialLink]], never raw user
 * input. A 400 with the server's own message is how an unparseable URL comes
 * back, which the screen pins under the field.
 *
 * `string`, NOT `string | null`, even though the wire would take a null: a link
 * once given can be changed but never removed, so there is no such thing as a
 * caller that clears one. Typed that way, the rule holds for every future
 * caller instead of being re-remembered per screen — omit the key to leave a
 * link alone.
 *
 * Returns the full refreshed profile so the caller can sync `sessionStore`
 * immediately — a names-only save can be the LAST step of a flow (no forced
 * email), so there's no later verify step to do that syncing instead, and for a
 * social link this response is the only place the saved value arrives.
 */
async function updateMe(details: {
  firstName?: string;
  lastName?: string;
  email?: string;
  socialMediaLink?: string;
}): Promise<{
  user: UserProfile;
  emailChallengeId: string | null;
}> {
  const response = await api.patch<PatchMeBody>('/me', details);
  if (!response.ok || !response.data) handleApiError(response);

  const body = response.data;
  return {
    user: toUserProfile(body),
    emailChallengeId: body.challengeId ?? null,
  };
}

/**
 * Delete the signed-in account. 🔒 The token is the only thing naming what gets
 * deleted — no body, no confirmation round-trip, no undo.
 *
 * 204, so there's nothing to read back. It kills EVERY token on the account, not
 * just this device's, which is why the caller must follow with `endSession` — the
 * credential in AsyncStorage is already dead by the time this returns.
 *
 * It does NOT cancel an active membership: the Razorpay mandate keeps charging an
 * account nobody can log into. Cancelling stays the user's call, so the screen
 * warns rather than doing it here.
 */
async function deleteMe(): Promise<void> {
  const response = await api.delete('/me');
  if (!response.ok) handleApiError(response);
}

export default {
  requestMobileOtp,
  verifyOtp,
  resendOtp,
  updateMe,
  fetchMe,
  deleteMe,
};
